import { useGameState } from '../GameProvider';
import gameService from '../gameService';
import './RulesViewer.css';

export default function RulesViewer({ onClose }) {
  const { gameMode, activeRules, uid, dealerUid, gameId, phase } = useGameState();
  const isDealer = uid === dealerUid;
  const canEdit = isDealer && (phase === 'lobby' || phase === 'results');
  const isNem = gameMode === 'nem' || gameMode === 'nemchi';
  const isChi = gameMode === 'chi' || gameMode === 'nemchi';

  const handleRuleChange = (key, value) => {
    gameService.updateRules(gameId, { [key]: value });
  };

  return (
    <div className="rules-overlay" onClick={onClose}>
      <div className="rules-modal" onClick={e => e.stopPropagation()}>
        <button className="rules-close" onClick={onClose}>✕</button>
        <h2>Rules — {gameMode?.toUpperCase()}</h2>

        {/* Scoring section */}
        <div className="rules-section">
          <h3>Scoring</h3>
          {isChi ? (
            <p>Per-row scoring with bonuses. Win each row independently. Bonus points for strong hands in specific rows.</p>
          ) : (
            <p>Majority-based. Win 2+ rows vs an opponent = +1 point. Lose 2+ rows = -1 point.</p>
          )}
        </div>

        {/* Hand arrangement */}
        <div className="rules-section">
          <h3>Arrangement</h3>
          <p>13 cards → Bottom (5) + Middle (5) + Top (3). Bottom must be stronger than Middle, Middle stronger than Top.</p>
        </div>

        {/* 5-card rankings */}
        <div className="rules-section">
          <h3>5-Card Rankings (Bottom & Middle)</h3>
          <ol reversed className="rules-rankings">
            <li>Five of a Kind (multi-deck)</li>
            <li>Royal Flush</li>
            <li>Straight Flush</li>
            <li>Four of a Kind</li>
            <li>Full House</li>
            <li>Flush</li>
            <li>Straight</li>
            <li>Three of a Kind</li>
            <li>Two Pair</li>
            <li>One Pair</li>
            <li>High Card</li>
          </ol>
        </div>

        {/* 3-card rankings */}
        <div className="rules-section">
          <h3>3-Card Rankings (Top)</h3>
          <ol reversed className="rules-rankings">
            <li>Three of a Kind</li>
            <li>One Pair</li>
            <li>High Card</li>
          </ol>
        </div>

        {/* Chi bonus table */}
        {isChi && (
          <div className="rules-section">
            <h3>Chi Bonuses</h3>
            <table className="rules-bonus-table">
              <thead><tr><th>Hand</th><th>Bottom</th><th>Middle</th><th>Top</th></tr></thead>
              <tbody>
                <tr><td>Three of a Kind</td><td>—</td><td>—</td><td>3</td></tr>
                <tr><td>Full House</td><td>1</td><td>2</td><td>—</td></tr>
                <tr><td>Four of a Kind</td><td>4</td><td>8</td><td>—</td></tr>
                <tr><td>Straight Flush</td><td>5</td><td>10</td><td>—</td></tr>
                <tr><td>Five of a Kind</td><td>5</td><td>10</td><td>—</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Nem section */}
        {isNem && (
          <div className="rules-section">
            <h3>Nem (Card Exchange)</h3>
            <ol>
              <li>Arrange your cards</li>
              <li>Drag a card to the discard zone</li>
              <li>Confirm discard — receive a new card from the deck</li>
              <li>Rearrange with your new card</li>
            </ol>
          </div>
        )}

        {/* Toggleable rules */}
        <div className="rules-section">
          <h3>Active Rules</h3>
          {renderTimerSelect(activeRules, canEdit, handleRuleChange)}
          {renderToggle('Violation Help', 'violationHelp', activeRules, canEdit, handleRuleChange)}
          {renderToggle('Sweep Bonus', 'sweepBonus', activeRules, canEdit, handleRuleChange)}
          {renderToggle('Individual Round House', 'individualRoundHouse', activeRules, canEdit, handleRuleChange)}
          {renderToggle('Auto-Win', 'autoWinEnabled', activeRules, canEdit, handleRuleChange)}
          {isChi && renderToggle('Bonus Hands', 'bonusHandsEnabled', activeRules, canEdit, handleRuleChange)}
          {isChi && renderToggle('Middle Multiplier (2×)', 'middleMultiplierEnabled', activeRules, canEdit, handleRuleChange)}
        </div>
      </div>
    </div>
  );
}

const TIMER_OPTIONS = [
  { value: '', label: 'Default (1½ min)' },
  { value: 120, label: '2 minutes' },
  { value: 180, label: '3 minutes' },
  { value: 240, label: '4 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 0, label: 'Unlimited' },
];

// Dealer-adjustable time limit for setting the hand. Applies from the next
// deal (rule changes mid-round are queued as pendingRules by the CF).
function renderTimerSelect(rules, canEdit, onChange) {
  const current = rules.arrangeTimerSeconds;
  const currentLabel = (TIMER_OPTIONS.find(o => o.value === (current ?? '')) || TIMER_OPTIONS[0]).label;
  return (
    <div className="rules-toggle">
      <span>Hand Timer</span>
      {canEdit ? (
        <select
          className="rules-timer-select"
          data-testid="rule-hand-timer"
          value={current ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange('arrangeTimerSeconds', v === '' ? null : parseInt(v, 10));
          }}
        >
          {TIMER_OPTIONS.map(o => (
            <option key={o.label} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <span className="rules-toggle-value on">{currentLabel}</span>
      )}
    </div>
  );
}

function renderToggle(label, key, rules, canEdit, onChange) {
  const value = rules[key];
  return (
    <div className="rules-toggle">
      <span>{label}</span>
      {canEdit ? (
        <button
          className={`rules-toggle-btn ${value ? 'rules-toggle-btn--on' : ''}`}
          onClick={() => onChange(key, !value)}
        >
          {value ? 'ON' : 'OFF'}
        </button>
      ) : (
        <span className={`rules-toggle-value ${value ? 'on' : 'off'}`}>
          {value ? 'ON' : 'OFF'}
        </span>
      )}
    </div>
  );
}
